import { Injectable } from '@angular/core';

export interface SitemapUrl {
  loc: string;
  lastmod?: string;
  changefreq?: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  priority?: number;
}

@Injectable({
  providedIn: 'root',
})
export class SitemapService {
  private readonly baseUrl = 'https://simvetmonitor.com';
  private readonly supportedLangs = ['en', 'es', 'pt', 'fr', 'de', 'it'];

  /**
   * Generate sitemap XML string
   */
  generateSitemap(): string {
    const urls = this.getPublicUrls();
    const currentDate = new Date().toISOString().split('T')[0];

    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"\n';
    xml += '        xmlns:xhtml="http://www.w3.org/1999/xhtml">\n';

    urls.forEach(url => {
      xml += '  <url>\n';
      xml += `    <loc>${this.baseUrl}${url.loc}</loc>\n`;
      
      if (url.lastmod) {
        xml += `    <lastmod>${url.lastmod}</lastmod>\n`;
      } else {
        xml += `    <lastmod>${currentDate}</lastmod>\n`;
      }

      if (url.changefreq) {
        xml += `    <changefreq>${url.changefreq}</changefreq>\n`;
      }

      if (url.priority !== undefined) {
        xml += `    <priority>${url.priority}</priority>\n`;
      }

      // Add hreflang alternatives for multilingual support
      this.supportedLangs.forEach(lang => {
        const langUrl = `${url.loc}${url.loc.includes('?') ? '&' : '?'}lang=${lang}`;
        xml += `    <xhtml:link rel="alternate" hreflang="${lang}" href="${this.baseUrl}${langUrl}" />\n`;
      });
      xml += `    <xhtml:link rel="alternate" hreflang="x-default" href="${this.baseUrl}${url.loc}" />\n`;

      xml += '  </url>\n';
    });

    xml += '</urlset>';
    return xml;
  }

  /**
   * Get list of public URLs to include in sitemap
   * Excludes protected routes (auth required)
   */
  private getPublicUrls(): SitemapUrl[] {
    const urls: SitemapUrl[] = [
      {
        loc: '/home',
        changefreq: 'weekly',
        priority: 1.0,
      },
      {
        loc: '/elearning',
        changefreq: 'weekly',
        priority: 0.9,
      },
      {
        loc: '/elearning/teacher',
        changefreq: 'weekly',
        priority: 0.8,
      },
      {
        loc: '/elearning/students',
        changefreq: 'weekly',
        priority: 0.8,
      },
    ];

    return urls;
  }

  /**
   * Generate sitemap index (for multiple sitemaps)
   */
  generateSitemapIndex(sitemaps: Array<{ loc: string; lastmod?: string }>): string {
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';

    sitemaps.forEach(sitemap => {
      xml += '  <sitemap>\n';
      xml += `    <loc>${this.baseUrl}${sitemap.loc}</loc>\n`;
      if (sitemap.lastmod) {
        xml += `    <lastmod>${sitemap.lastmod}</lastmod>\n`;
      }
      xml += '  </sitemap>\n';
    });

    xml += '</sitemapindex>';
    return xml;
  }
}

